/**
 * @license
 * Portions derived from voiceorb by aguscruiz
 * https://github.com/aguscruiz/voiceorb
 * Copyright (c) aguscruiz - MIT License
 */

/**
 * VoiceOrb - animated orb for voice input.
 *
 * Rendering is handled by voiceorb.html (WebGL shaders) loaded in:
 *   - Web: an iframe (audio levels sent via postMessage)
 *   - Android: native ViewManager wrapping Android WebView (audio levels pushed
 *     via evaluateJavascript from Choreographer callback)
 *
 * The HTML file is the single source of truth for shaders, configs, animation,
 * and real-time face selection based on audio levels. The React layer only
 * sends the "base" face variant (inactive/idle/thinking) via setFace messages;
 * the HTML overrides it with listening/talking faces based on mic/audio levels.
 */

import React, { useEffect, useRef } from "react";
import {
  View,
  Pressable,
  Text,
  StyleSheet,
  Platform,
  UIManager,
  findNodeHandle,
  requireNativeComponent,
} from "react-native";
import { useSocketStore } from "../../store/socketStore";
import { useAudioStore } from "../../store/audioStore";
import { useFaceStore } from "../../store/faceStore";
import { getAudioLevel as getFTUEAudioLevel } from "../../audio/FTUEAudioPlayer";
import { colors } from "../../styles";
import voiceorbHtml from "./voiceorbHtml";

/* -------------------------------------------------------------------------- */
/* Shared: evalJs bridge for Android native WebView                           */
/* -------------------------------------------------------------------------- */

/** Send JS to the native VoiceOrbView via the evalJs command (no-op on old builds). */
function dispatchEvalJs(ref: React.RefObject<any>, js: string) {
  const handle = findNodeHandle(ref.current);
  if (handle != null) {
    UIManager.dispatchViewManagerCommand(handle, "evalJs", [js]);
  }
}

/* Native VoiceOrb view - only resolved on Android where the ViewManager is registered. */
const NativeVoiceOrbView: any =
  Platform.OS === "android"
    ? requireNativeComponent("VoiceOrbView")
    : null;

/* -------------------------------------------------------------------------- */
/* VoiceOrb — main component                                                  */
/* -------------------------------------------------------------------------- */

interface VoiceOrbProps {
  /** Display diameter in px. */
  size?: number;
}

export default function VoiceOrb({ size = 140 }: VoiceOrbProps) {
  const { isVoiceActive, isConnected, muteVoice, unmuteVoice, startVoice } = useSocketStore();
  const accessBlocked = useSocketStore((s) => s.accessBlocked);
  const orbMode = useFaceStore((s) => s.orbMode);
  const ftueVariant = useFaceStore((s) => s.ftueVariant);
  // Dim when disconnected/blocked and not on FTUE/landing (ftueVariant set stays full opacity)
  const orbOpacity = (orbMode === "inactive" && (!isConnected || accessBlocked) && !ftueVariant) ? 0.5 : 1;

  const handlePress = () => {
    if (!isConnected) return;
    // Mute/unmute keeps the Bedrock stream alive for 7s — instant toggle
    // without HTTP/2 race conditions. Full startVoice only if no stream exists.
    if (isVoiceActive) muteVoice();
    else unmuteVoice();
  };

  const a11yLabel = !isConnected
    ? "Voice unavailable - not connected"
    : isVoiceActive ? "Stop voice session" : "Start voice session";

  /* -- Web: iframe loading voiceorb.html, audio levels via postMessage -- */
  if (Platform.OS === "web") {
    return (
      <Pressable onPress={handlePress} disabled={!isConnected} testID="voice-orb" accessibilityLabel={a11yLabel} accessibilityRole="button" style={{ cursor: isConnected ? "pointer" : "default", opacity: orbOpacity } as any}>
        <WebOrb size={size} />
      </Pressable>
    );
  }

  /* -- Android: native WebView ViewManager, state pushed via Choreographer -- */
  if (Platform.OS === "android" && NativeVoiceOrbView) {
    return (
      <Pressable onPress={handlePress} disabled={!isConnected} testID="voice-orb" accessibilityLabel={a11yLabel} accessibilityRole="button" style={{ width: size, height: size, opacity: orbOpacity }}>
        <AndroidOrb size={size} isVoiceActive={isVoiceActive} isConnected={isConnected} />
      </Pressable>
    );
  }

  /* -- Other native platforms: simple fallback -- */
  return (
    <Pressable
      onPress={handlePress}
      disabled={!isConnected}
      testID="voice-orb"
      accessibilityLabel={a11yLabel}
      accessibilityRole="button"
      style={[styles.fallback, { width: size, height: size, borderRadius: size / 2, opacity: orbOpacity }]}
    >
      <Text style={styles.fallbackIcon}>{isVoiceActive ? "\u25A0" : "\uD83C\uDFA4"}</Text>
    </Pressable>
  );
}

/* -------------------------------------------------------------------------- */
/* Android: native WebView + evalJs for base face variant                     */
/* -------------------------------------------------------------------------- */

function AndroidOrb({ size, isVoiceActive, isConnected }: { size: number; isVoiceActive: boolean; isConnected: boolean }) {
  const nativeRef = useRef(null);
  const variant = useFaceStore((s) => s.variant);
  const orbMode = useFaceStore((s) => s.orbMode);
  const nativeScale = 2.0;
  const nativeSize = Math.round(size * nativeScale);
  const nativeOffset = Math.round((nativeSize - size) / 2);

  useEffect(() => {
    dispatchEvalJs(nativeRef, `setFace('${variant}')`);
  }, [variant]);

  return (
    <NativeVoiceOrbView
      ref={nativeRef}
      isVoiceActive={isVoiceActive}
      isConnected={isConnected}
      orbMode={orbMode}
      orbHtml={voiceorbHtml}
      style={{ width: nativeSize, height: nativeSize, position: "absolute", top: -nativeOffset, left: -nativeOffset }}
      pointerEvents="none"
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Web: iframe + requestAnimationFrame audio level pump                       */
/* -------------------------------------------------------------------------- */

function WebOrb({ size }: { size: number }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Audio level pump: postMessage to srcdoc iframe each frame.
  // srcdoc iframes have origin "null" so targetOrigin must be "*".
  // This is safe because we control the iframe content (it's our srcdoc).
  useEffect(() => {
    let rafId = 0;

    function tick() {
      rafId = requestAnimationFrame(tick);
      const orbMode = useFaceStore.getState().orbMode;
      const player = useAudioStore.getState().audioPlayer;
      const audioLevel = orbMode === "animated"
        ? getFTUEAudioLevel()
        : player?.getAudioLevel() ?? 0;
      const processor = useAudioStore.getState().audioProcessor;
      const micLevel = processor?.getMicLevel() ?? 0;
      const { isVoiceActive, isConnected } = useSocketStore.getState();
      const effectiveOrbMode = orbMode === "inactive" && isVoiceActive ? "active" : orbMode;

      iframeRef.current?.contentWindow?.postMessage({
        type: "orbState",
        audioLevel,
        micLevel,
        isActive: isVoiceActive,
        isConnected,
        orbMode: effectiveOrbMode,
      }, "*");
      iframeRef.current?.contentWindow?.postMessage({
        type: "setFace",
        variant: useFaceStore.getState().variant,
      }, "*");
    }
    tick();
    return () => cancelAnimationFrame(rafId);
  }, []);

  if (!voiceorbHtml) return null;

  const iframeScale = 2.0;
  const iframeSize = Math.round(size * iframeScale);
  const offset = Math.round((iframeSize - size) / 2);

  return (
    <div style={{ width: size, height: size, position: "relative" as any }}>
      <iframe
        ref={iframeRef}
        srcDoc={voiceorbHtml}
        style={{ width: iframeSize, height: iframeSize, border: "none", background: "transparent", display: "block", pointerEvents: "none", position: "absolute" as any, top: -offset, left: -offset }}
        title="Voice Orb"
        // @ts-ignore - React doesn't type allowTransparency but browsers support it
        allowtransparency="true"
      />
    </div>
  );
}

const styles = StyleSheet.create({
  fallback: {
    backgroundColor: colors.accent,
    alignItems: "center",
    justifyContent: "center",
  },
  fallbackIcon: {
    color: colors.white,
    fontSize: 24,
  },
});
